import { Component, NgZone, OnDestroy, OnInit } from '@angular/core'
import { SpinnerService } from '../../services/spinner/spinner.service'
import { LoggerService } from '../../services/logger/logger.service'
import { UserDataService } from '../../services/user-data/user-data.service'
import { GlobalNotification, GlobalState } from '../../app.state'
import { PiReportsService } from '../../services/api/services/pi-reports.service'
import { TableWithFilters } from '../../shared/absracts/table-with-filters'
import { MethodsService, XlsService } from '../../services/methods/methods.service'

@Component({
  selector: '',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class PIReportsComponent extends TableWithFilters implements OnInit, OnDestroy {
  isEligibleToGenerate: boolean = false

  readonly title = 'CPC Reports'
  paginationName = ''

  availableReports: PiExtendedReportMetadata[] = []
  selectedReport: PiExtendedReportMetadata[] = []
  reportDescription: string
  reportData: StandardJson[]
  reportDataSorted: StandardJson[] = []
  reportNoDataFlag: boolean = false
  currentGeneratedReportType?: string

  //table
  tableHeaders: TableHeader[]
  currentSortBy: string
  _isRefreshing: boolean = false
  filterValue: string = ''
  currentItemsPerPage = 50

  //filters & pagination
  readonly timeFilter = MethodsService.timeObject().slice(3)
  currentTime = this.timeFilter[4]

  constructor (private _userData: UserDataService, private _reports: PiReportsService, private _zone: NgZone, private _state: GlobalState) {
    super()
    this.isEligibleToGenerate = this._userData.hasPermissions('/report', UserPermission.READ_ONLY)
  }

  ngOnDestroy (): void {
  }

  ngOnInit (): void {
    this.refreshTable!()
    this._state.subscribe(GlobalNotification.BACKGROUND_REFRESH, () => this.refreshTable(true))
  }

  clearSelections (skipSelectedReport: boolean = false): void {
    !skipSelectedReport ? this.selectedReport.length = 0 : null
    this.reportDescription = ''
    this.filterValue = ''
    this.reportData = undefined
    this.reportDataSorted = undefined
    this.currentSortBy = undefined
    this.currentGeneratedReportType = undefined
    this.reportNoDataFlag = false
  }

  hasTimeRange (type: string) {
    return !!this.availableReports.find(r => r.type == type)?.timeRange
  }

  async onSelectReport (): Promise<void> {
    if (!this.selectedReport.length) {
      return this.clearSelections()
    }

    const type = this.selectedReport[0].type

    if (this.currentGeneratedReportType && type != this.currentGeneratedReportType) {
      this.clearSelections(true)
    }

    this.reportDescription = this.availableReports.find(r => r.type == this.selectedReport[0].type)?.description
  }

  async refreshTable (background: boolean = false, ignoreSelected: boolean = false): Promise<void> {
    if (this._isRefreshing) {
      return
    }
    this._isRefreshing = true
    try {
      if (!background) {
        this.clearSelections()
        SpinnerService.spin('mini')
      }
      const [
        reports
      ] = await Promise.all([
        this._reports.getMetadata()
      ].filter(x => !!x) as PromiseLike<any>[])

      this.availableReports = reports

    } catch (e) {
      LoggerService.error(e)
    } finally {
      if (!background) {
        SpinnerService.stop('mini')
      }
      this._zone.run(() => {})
      this._isRefreshing = false
    }

  }

  async generateReport () {
    SpinnerService.spin('mini')
    const type = this.selectedReport[0].type
    try {
      this.reportData = await this._reports.getReport(type, {
        fromDate: this.currentTime.time(),
        toDate: new Date().getTime(),
      })
      this.reportNoDataFlag = this.reportData.length == 0
      this.currentGeneratedReportType = type
      const width = Math.floor(100 / Object.keys(this.reportData[0] || {}).length)
      this.tableHeaders = Object.keys(this.reportData[0] || {}).map(name => ({ name, sortable: true, width }))
      this.sortTable()
    } catch (e) {
      console.error(e)
      MethodsService.toast('error', `Error generating report ${type}`, e.toString(), 8)
    } finally {
      SpinnerService.stop('mini')
      this._zone.run(() => {})
    }
  }

  sortTable () {
    if (!this.currentSortBy) {
      this.currentSortBy = this.tableHeaders[0]?.name || ''
    }
    const isNum = typeof this.reportData[0]?.[this.currentSortBy] == 'number'
    const sorted = this.reportData
      .filter(item => {
        const filterValue = this.filterValue.trim().toLowerCase()
        if (!filterValue) {
          return true
        }
        return Object.values(item).some(value => {
          if (typeof value == 'string') {
            return value.toLowerCase().includes(filterValue)
          }
          return false
        })
      })
      .sort((a, b) => {
        const _a = a[this.currentSortBy]
        const _b = b[this.currentSortBy]
        if (isNum) {
          return _a - _b
        }
        return _a.localeCompare(_b)
      })
    if (this.isSortReversed) {
      sorted.reverse()
    }
    this.reportDataSorted = sorted.slice((this.currentPage - 1) * this.currentItemsPerPage, this.currentPage * this.currentItemsPerPage)
    this.numOfAvailableDocs = sorted.length
  }

  downloadCSV () {
    XlsService.export(this.reportData, this.selectedReport[0].type, 'csv')
  }

  downloadJSON () {
    MethodsService.downloadAsFile(JSON.stringify(this.reportData, null, 2), `${this.selectedReport[0].type}_${MethodsService.cxDateFormat().filenameDate}.json`)
  }

}
